/**
 * The PadLab project model, ported one-for-one from the Swift sources in
 * `native/PadLab/Model/`. Field names, defaults, clamp ranges and user-facing wording are
 * identical on both platforms, so a `.padlab` file written by the iPhone app opens in the
 * PWA and vice versa.
 *
 * Two encoding decisions make that round trip work:
 *
 * - **Dates are ISO-8601 strings, not `Date` objects.** Swift's `JSONEncoder` with
 *   `.iso8601` writes `2026-08-12T09:41:00Z` — internet date-time, *no fractional seconds*
 *   — and its decoder rejects anything else. Keeping the in-memory value as the same string
 *   means the model is plain JSON at every moment: `structuredClone` into a worker,
 *   IndexedDB, undo snapshots and `encodeProject` all behave identically, and nothing can
 *   accidentally serialise milliseconds Swift would refuse. `schema.ts` owns the
 *   formatting.
 * - **Identifiers are upper-case UUID strings**, because that is what Foundation's `UUID`
 *   emits. Foundation parses lower case too, so a web-created id is readable on iOS, but
 *   matching the case keeps two encodings of the same project byte-identical.
 *
 * Swift's optionals are absent keys in JSON (JSONEncoder omits `nil`), so optionals here
 * are `?:` properties and are omitted rather than written as `null`.
 */

// MARK: - Primitives

/** Upper-case UUID string, e.g. `"3F2504E0-4F89-41D3-9A0C-0305E82C3301"`. */
export type UUID = string;

/** ISO-8601 internet date-time in UTC with no fractional seconds: `2026-08-12T09:41:00Z`. */
export type ISODate = string;

/** Seconds. Named after Foundation's `TimeInterval` so the two models read the same. */
export type TimeInterval = number;

const HEX = '0123456789ABCDEF';

/**
 * `crypto.randomUUID` is unavailable outside a secure context, which is exactly the case
 * when testing on a physical iPhone over a plain-http LAN address, so the fallback is not
 * theoretical. Both paths produce an upper-case v4 UUID.
 */
export function newUUID(): UUID {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().toUpperCase();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  let out = '';
  for (let index = 0; index < 16; index++) {
    if (index === 4 || index === 6 || index === 8 || index === 10) out += '-';
    const byte = bytes[index]!;
    out += HEX[byte >> 4]! + HEX[byte & 0x0f]!;
  }
  return out;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

/**
 * Clamp that also survives `NaN`, `Infinity` and a JSON `null` that arrived where a number
 * was expected — a hand-edited or truncated project must never push an audio unit into an
 * undefined state.
 */
export function clampFinite(
  value: number,
  minimum: number,
  maximum: number,
  fallback: number,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, minimum), maximum);
}

// MARK: - Pad trigger + playback semantics

/**
 * How a pad responds to a touch. These are behaviours, not effects: they decide when a
 * voice starts, when it stops, and what happens to a voice that is already sounding when
 * the pad is hit again.
 */
export type PlaybackMode = 'oneShot' | 'hold' | 'gate' | 'toggle' | 'loop';

export const PLAYBACK_MODES: readonly PlaybackMode[] = ['oneShot', 'hold', 'gate', 'toggle', 'loop'];

export function playbackModeDisplayName(mode: PlaybackMode): string {
  switch (mode) {
    case 'oneShot':
      return 'One-Shot';
    case 'hold':
      return 'Hold';
    case 'gate':
      return 'Gate';
    case 'toggle':
      return 'Toggle';
    case 'loop':
      return 'Loop';
  }
}

/**
 * The one-line gloss under a mode's name in the pad menu.
 *
 * Short by measurement, not by taste: the detail column of a menu row is 106 px on a 320 px
 * screen, and these sentences ran to 63 characters, which wrapped to four lines against a
 * one-line title and made the row unreadable. The pad menu is the only caller.
 */
export function playbackModeSummary(mode: PlaybackMode): string {
  switch (mode) {
    case 'oneShot':
      return 'Plays it all';
    case 'hold':
      return 'While held';
    case 'gate':
      return 'Fades on lift';
    case 'toggle':
      return 'Tap on and off';
    case 'loop':
      return 'Repeats';
  }
}

/** True when lifting the finger must end the voice. */
export function playbackModeStopsOnRelease(mode: PlaybackMode): boolean {
  return mode === 'hold' || mode === 'gate';
}

/** What a re-trigger does to a voice that is already sounding on this pad. */
export type RetriggerBehaviour = 'restart' | 'layer' | 'ignore' | 'resume';

export const RETRIGGER_BEHAVIOURS: readonly RetriggerBehaviour[] = [
  'restart',
  'layer',
  'ignore',
  'resume',
];

export function retriggerDisplayName(behaviour: RetriggerBehaviour): string {
  switch (behaviour) {
    case 'restart':
      return 'Restart';
    case 'layer':
      return 'Layer';
    case 'ignore':
      return 'Ignore';
    case 'resume':
      return 'Continue';
  }
}

/** Same 106 px column as `playbackModeSummary`, same reason. The pad menu is the only caller. */
export function retriggerSummary(behaviour: RetriggerBehaviour): string {
  switch (behaviour) {
    case 'restart':
      return 'Starts over';
    case 'layer':
      return 'Stacks a copy';
    // Not "waits its turn": the new hit is dropped, it does not play later.
    case 'ignore':
      return 'Lets it finish';
    case 'resume':
      return 'Picks up again';
  }
}

/**
 * Grid density for a bank. Values are (columns, rows) in portrait; landscape swaps them
 * unless the layout is `adaptive`.
 */
export type GridSize = 'g2x3' | 'g3x3' | 'g3x4' | 'g4x4' | 'g4x6' | 'adaptive';

export const GRID_SIZES: readonly GridSize[] = ['g2x3', 'g3x3', 'g3x4', 'g4x4', 'g4x6', 'adaptive'];

/**
 * Nominal shape of an adaptive bank.
 *
 * Adaptive means the *layout* follows the screen, not that the bank changes size underneath
 * the user — pads are addressed by slot and a capacity that moved with the orientation would
 * make a pad disappear when a phone is turned. So the bank always holds twenty-four slots
 * and `adaptiveGrid()` decides how they are arranged for the viewport in front of them.
 */
const ADAPTIVE_COLUMNS = 4;
const ADAPTIVE_ROWS = 6;

export function gridColumns(grid: GridSize): number {
  switch (grid) {
    case 'g2x3':
      return 2;
    case 'g3x3':
    case 'g3x4':
      return 3;
    case 'g4x4':
    case 'g4x6':
      return 4;
    case 'adaptive':
      return ADAPTIVE_COLUMNS;
  }
}

export function gridRows(grid: GridSize): number {
  switch (grid) {
    case 'g2x3':
    case 'g3x3':
      return 3;
    case 'g3x4':
      return 4;
    case 'g4x4':
      return 4;
    case 'g4x6':
      return 6;
    case 'adaptive':
      return ADAPTIVE_ROWS;
  }
}

/**
 * How an adaptive bank should be laid out for a viewport of this shape.
 *
 * Twenty-four pads, arranged so a tile stays close to square and the whole grid fills the
 * space: a portrait phone gets 4 × 6, a landscape phone 6 × 4, a wide tablet 8 × 3. Anything
 * else — every fixed size — is returned unchanged, so callers can use this for every grid.
 */
export function adaptiveGrid(
  grid: GridSize,
  viewport: { width: number; height: number },
): { columns: number; rows: number } {
  if (grid !== 'adaptive') return { columns: gridColumns(grid), rows: gridRows(grid) };
  const capacity = ADAPTIVE_COLUMNS * ADAPTIVE_ROWS;
  const aspect = viewport.height <= 0 ? 1 : viewport.width / viewport.height;
  // Factor pairs of 24, from tallest to widest. The closest one to the viewport's own
  // aspect ratio is the arrangement whose tiles come out nearest square.
  const options = [
    { columns: 3, rows: 8 },
    { columns: 4, rows: 6 },
    { columns: 6, rows: 4 },
    { columns: 8, rows: 3 },
  ];
  let best = options[1]!;
  let bestError = Number.POSITIVE_INFINITY;
  for (const option of options) {
    if (option.columns * option.rows !== capacity) continue;
    const error = Math.abs(Math.log(option.columns / option.rows) - Math.log(Math.max(0.05, aspect)));
    if (error < bestError) {
      bestError = error;
      best = option;
    }
  }
  return best;
}

export function gridCapacity(grid: GridSize): number {
  return gridColumns(grid) * gridRows(grid);
}

export function gridDisplayName(grid: GridSize): string {
  if (grid === 'adaptive') return 'Auto';
  return `${gridColumns(grid)} × ${gridRows(grid)}`;
}

/**
 * Everything a pad tile can be showing. The board renders exactly one of these per pad and
 * every one is distinguishable without relying on colour alone.
 */
export type PadState =
  | 'empty'
  | 'loaded'
  | 'pressed'
  | 'playing'
  | 'looping'
  | 'queued'
  | 'recording'
  | 'muted'
  | 'soloed'
  | 'processing'
  | 'missingSource'
  | 'decodeFailed';

export const PAD_STATES: readonly PadState[] = [
  'empty',
  'loaded',
  'pressed',
  'playing',
  'looping',
  'queued',
  'recording',
  'muted',
  'soloed',
  'processing',
  'missingSource',
  'decodeFailed',
];

/**
 * Glyph shown in the pad's status corner; `null` means "no badge".
 *
 * The identifiers are SF Symbol names, exactly as on iOS. The web has no SF Symbols, but a
 * `Pad.symbolName` chosen on the phone is stored in the project and has to render here
 * anyway, so the icon layer already owns an SF-name → inline-SVG mapping. Returning the
 * same names keeps that one mapping instead of two vocabularies.
 */
export function padStateGlyph(state: PadState): string | null {
  switch (state) {
    case 'empty':
      return 'plus';
    case 'loaded':
    case 'pressed':
      return null;
    case 'playing':
      return 'play.fill';
    case 'looping':
      return 'repeat';
    case 'queued':
      return 'clock.fill';
    case 'recording':
      return 'record.circle.fill';
    case 'muted':
      return 'speaker.slash.fill';
    case 'soloed':
      return 'headphones';
    case 'processing':
      return 'hourglass';
    case 'missingSource':
      return 'questionmark.folder.fill';
    case 'decodeFailed':
      return 'exclamationmark.triangle.fill';
  }
}

export function padStateIsErrored(state: PadState): boolean {
  return state === 'missingSource' || state === 'decodeFailed';
}

export function padStateIsSounding(state: PadState): boolean {
  return state === 'playing' || state === 'looping' || state === 'pressed';
}

export function padStateAccessibilityDescription(state: PadState): string {
  switch (state) {
    case 'empty':
      return 'Empty pad, double tap to add a sound';
    case 'loaded':
      return 'Ready';
    case 'pressed':
      return 'Pressed';
    case 'playing':
      return 'Playing';
    case 'looping':
      return 'Looping';
    case 'queued':
      return 'Queued, waiting for the next beat';
    case 'recording':
      return 'Recording';
    case 'muted':
      return 'Muted';
    case 'soloed':
      return 'Soloed';
    case 'processing':
      return 'Processing';
    case 'missingSource':
      return 'Sound file is missing';
    case 'decodeFailed':
      return 'Sound file could not be decoded';
  }
}

/** Quantisation grid used for queued pad triggers and for the sequencer. */
export type Quantize =
  | 'off'
  | 'bar'
  | 'half'
  | 'quarter'
  | 'eighth'
  | 'sixteenth'
  | 'triplet8'
  | 'triplet16';

export const QUANTIZE_OPTIONS: readonly Quantize[] = [
  'off',
  'bar',
  'half',
  'quarter',
  'eighth',
  'sixteenth',
  'triplet8',
  'triplet16',
];

/** Length of one grid division in beats (quarter notes). `null` when quantisation is off. */
export function quantizeBeats(quantize: Quantize): number | null {
  switch (quantize) {
    case 'off':
      return null;
    case 'bar':
      return 4;
    case 'half':
      return 2;
    case 'quarter':
      return 1;
    case 'eighth':
      return 0.5;
    case 'sixteenth':
      return 0.25;
    case 'triplet8':
      return 1 / 3;
    case 'triplet16':
      return 1 / 6;
  }
}

export function quantizeDisplayName(quantize: Quantize): string {
  switch (quantize) {
    case 'off':
      return 'Off';
    case 'bar':
      return '1 Bar';
    case 'half':
      return '1/2';
    case 'quarter':
      return '1/4';
    case 'eighth':
      return '1/8';
    case 'sixteenth':
      return '1/16';
    case 'triplet8':
      return '1/8T';
    case 'triplet16':
      return '1/16T';
  }
}

/**
 * Colour identity assigned to a pad. Stored as a symbolic name so the palette can be
 * re-tuned without rewriting user projects.
 */
export type PadColor =
  | 'magenta'
  | 'violet'
  | 'indigo'
  | 'cyan'
  | 'mint'
  | 'acid'
  | 'amber'
  | 'coral'
  | 'slate';

export const PAD_COLORS: readonly PadColor[] = [
  'magenta',
  'violet',
  'indigo',
  'cyan',
  'mint',
  'acid',
  'amber',
  'coral',
  'slate',
];

/** Hex value, identical to `PadColor.tint` in `Design/Tokens.swift`. */
export function padColorTint(color: PadColor): string {
  switch (color) {
    case 'magenta':
      return '#EC5CFF';
    case 'violet':
      return '#7B3FF2';
    case 'indigo':
      return '#4B3BE8';
    case 'cyan':
      return '#3DD9FF';
    case 'mint':
      return '#2FEFB4';
    case 'acid':
      return '#DCFF33';
    case 'amber':
      return '#F5C518';
    case 'coral':
      return '#FF5C7A';
    case 'slate':
      return '#767C90';
  }
}

/** Text colour that meets contrast on a fully saturated tile of this tint. */
export function padColorOnTint(color: PadColor): string {
  switch (color) {
    case 'acid':
    case 'amber':
    case 'mint':
    case 'cyan':
      return '#0A0A0C';
    case 'magenta':
    case 'violet':
    case 'indigo':
    case 'coral':
    case 'slate':
      return '#FFFFFF';
  }
}

/** `var(--accent-…)` reference, so a themed surface can inherit rather than hard-code. */
export function padColorVariable(color: PadColor): string {
  return `var(--accent-${color})`;
}

export function padColorDisplayName(color: PadColor): string {
  return color.charAt(0).toUpperCase() + color.slice(1);
}

/** Audio file container the app can write. */
export type ExportFormat = 'wav24' | 'wav16' | 'm4a';

export const EXPORT_FORMATS: readonly ExportFormat[] = ['wav24', 'wav16', 'm4a'];

export function exportFormatFileExtension(format: ExportFormat): string {
  return format === 'm4a' ? 'm4a' : 'wav';
}

export function exportFormatDisplayName(format: ExportFormat): string {
  switch (format) {
    case 'wav24':
      return 'WAV · 24-bit';
    case 'wav16':
      return 'WAV · 16-bit';
    case 'm4a':
      return 'M4A · AAC 256k';
  }
}

export function exportFormatSummary(format: ExportFormat): string {
  switch (format) {
    case 'wav24':
      return 'Lossless master quality. Largest files.';
    case 'wav16':
      return 'Lossless CD quality. Widely compatible.';
    case 'm4a':
      return 'Compressed. Best for sharing and messaging.';
  }
}

/** Scope of an export job. */
export type ExportScope = 'mix' | 'song' | 'stems' | 'selectedSample' | 'projectPackage' | 'soundPack';

export const EXPORT_SCOPES: readonly ExportScope[] = [
  'mix',
  'song',
  'stems',
  'selectedSample',
  'projectPackage',
  'soundPack',
];

export function exportScopeDisplayName(scope: ExportScope): string {
  switch (scope) {
    case 'mix':
      return 'Full Mix';
    case 'song':
      return 'Complete Song';
    case 'stems':
      return 'Individual Stems';
    case 'selectedSample':
      return 'This Sample';
    case 'projectPackage':
      return 'Project Backup';
    case 'soundPack':
      return 'Sound Pack';
  }
}

/**
 * Where a sample originally came from. Surfaced in the library and used for licensing
 * checks before publishing.
 */
export type SampleOrigin =
  | 'microphone'
  | 'filesImport'
  | 'shareSheet'
  | 'videoExtraction'
  | 'synthesised'
  | 'bundledPack'
  | 'downloadedPack'
  | 'renderedBounce';

export const SAMPLE_ORIGINS: readonly SampleOrigin[] = [
  'microphone',
  'filesImport',
  'shareSheet',
  'videoExtraction',
  'synthesised',
  'bundledPack',
  'downloadedPack',
  'renderedBounce',
];

export function sampleOriginDisplayName(origin: SampleOrigin): string {
  switch (origin) {
    case 'microphone':
      return 'Recorded';
    case 'filesImport':
      return 'Imported';
    case 'shareSheet':
      return 'Shared In';
    case 'videoExtraction':
      return 'From Video';
    case 'synthesised':
      return 'Synthesised';
    case 'bundledPack':
      return 'Starter Pack';
    case 'downloadedPack':
      return 'Downloaded';
    case 'renderedBounce':
      return 'Bounced';
  }
}

/**
 * Content the user did not author themselves needs a licence confirmation before it can be
 * published to the marketplace.
 */
export function sampleOriginRequiresLicenceConfirmationToPublish(origin: SampleOrigin): boolean {
  switch (origin) {
    case 'microphone':
    case 'synthesised':
    case 'renderedBounce':
      return false;
    case 'filesImport':
    case 'shareSheet':
    case 'videoExtraction':
    case 'bundledPack':
    case 'downloadedPack':
      return true;
  }
}

// MARK: - Effect parameter descriptors

export type EffectParameterScale = 'linear' | 'logarithmic' | 'stepped' | 'toggle';

export interface EffectParameter {
  readonly key: string;
  readonly name: string;
  readonly min: number;
  readonly max: number;
  readonly defaultValue: number;
  readonly unit: string;
  readonly scale: EffectParameterScale;
  /**
   * When false the randomiser never touches this parameter (a seed, a tempo-sync switch)
   * unless the user explicitly unlocks it.
   */
  readonly randomisable: boolean;
}

function parameter(
  key: string,
  name: string,
  min: number,
  max: number,
  defaultValue: number,
  unit = '',
  scale: EffectParameterScale = 'linear',
  randomisable = true,
): EffectParameter {
  return { key, name, min, max, defaultValue, unit, scale, randomisable };
}

export function effectParameterClamp(descriptor: EffectParameter, value: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return descriptor.defaultValue;
  if (descriptor.scale === 'stepped') {
    return clamp(Math.round(value), descriptor.min, descriptor.max);
  }
  if (descriptor.scale === 'toggle') return value >= 0.5 ? 1 : 0;
  return clamp(value, descriptor.min, descriptor.max);
}

/** Maps a 0…1 knob position onto the parameter's own range, honouring its scale. */
export function effectParameterDenormalize(
  descriptor: EffectParameter,
  position: number,
): number {
  const p = clampFinite(position, 0, 1, 0);
  switch (descriptor.scale) {
    case 'linear':
      return descriptor.min + (descriptor.max - descriptor.min) * p;
    case 'stepped':
      return Math.round(descriptor.min + (descriptor.max - descriptor.min) * p);
    case 'toggle':
      return p >= 0.5 ? 1 : 0;
    case 'logarithmic': {
      const lo = Math.max(descriptor.min, 0.0001);
      const hi = Math.max(descriptor.max, lo * 1.0001);
      return lo * Math.pow(hi / lo, p);
    }
  }
}

/** Inverse of `effectParameterDenormalize`. */
export function effectParameterNormalize(descriptor: EffectParameter, value: number): number {
  const v = effectParameterClamp(descriptor, value);
  switch (descriptor.scale) {
    case 'linear':
    case 'stepped':
      return descriptor.max > descriptor.min
        ? (v - descriptor.min) / (descriptor.max - descriptor.min)
        : 0;
    case 'toggle':
      return v >= 0.5 ? 1 : 0;
    case 'logarithmic': {
      const lo = Math.max(descriptor.min, 0.0001);
      const hi = Math.max(descriptor.max, lo * 1.0001);
      if (v <= 0) return 0;
      return Math.log(v / lo) / Math.log(hi / lo);
    }
  }
}

/** Human-readable value for labels and VoiceOver. */
export function effectParameterFormat(descriptor: EffectParameter, value: number): string {
  const v = effectParameterClamp(descriptor, value);
  if (descriptor.scale === 'toggle') return v >= 0.5 ? 'On' : 'Off';
  if (descriptor.scale === 'stepped') {
    const whole = String(Math.trunc(v));
    return descriptor.unit === '' ? whole : `${whole} ${descriptor.unit}`;
  }
  const magnitude = Math.abs(v);
  const digits = magnitude >= 100 ? 0 : magnitude >= 10 ? 1 : 2;
  const number = v.toFixed(digits);
  return descriptor.unit === '' ? number : `${number} ${descriptor.unit}`;
}

// MARK: - Effect kinds

export type EffectKind =
  | 'eq'
  | 'highPass'
  | 'lowPass'
  | 'bandPass'
  | 'compressor'
  | 'limiter'
  | 'noiseGate'
  | 'distortion'
  | 'bitCrush'
  | 'sampleRateReduce'
  | 'ringMod'
  | 'reverb'
  | 'delay'
  | 'chorus'
  | 'flanger'
  | 'tremolo'
  | 'pitchShift'
  | 'formantShift'
  | 'timeStretch'
  | 'harmoniser'
  | 'autoTune'
  | 'stutter'
  | 'glitch'
  | 'gainTrim';

export const EFFECT_KINDS: readonly EffectKind[] = [
  'eq',
  'highPass',
  'lowPass',
  'bandPass',
  'compressor',
  'limiter',
  'noiseGate',
  'distortion',
  'bitCrush',
  'sampleRateReduce',
  'ringMod',
  'reverb',
  'delay',
  'chorus',
  'flanger',
  'tremolo',
  'pitchShift',
  'formantShift',
  'timeStretch',
  'harmoniser',
  'autoTune',
  'stutter',
  'glitch',
  'gainTrim',
];

export type EffectCategory =
  | 'tone'
  | 'dynamics'
  | 'colour'
  | 'space'
  | 'pitch'
  | 'rhythmic'
  | 'utility';

export const EFFECT_CATEGORIES: readonly EffectCategory[] = [
  'tone',
  'dynamics',
  'colour',
  'space',
  'pitch',
  'rhythmic',
  'utility',
];

export function effectCategoryDisplayName(category: EffectCategory): string {
  switch (category) {
    case 'tone':
      return 'Tone';
    case 'dynamics':
      return 'Dynamics';
    case 'colour':
      return 'Colour';
    case 'space':
      return 'Space';
    case 'pitch':
      return 'Pitch & Time';
    case 'rhythmic':
      return 'Rhythmic';
    case 'utility':
      return 'Utility';
  }
}

export function effectKindDisplayName(kind: EffectKind): string {
  switch (kind) {
    case 'eq':
      return 'Parametric EQ';
    case 'highPass':
      return 'High-Pass';
    case 'lowPass':
      return 'Low-Pass';
    case 'bandPass':
      return 'Band-Pass';
    case 'compressor':
      return 'Compressor';
    case 'limiter':
      return 'Limiter';
    case 'noiseGate':
      return 'Noise Gate';
    case 'distortion':
      return 'Distortion';
    case 'bitCrush':
      return 'Bit Crush';
    case 'sampleRateReduce':
      return 'Sample Rate';
    case 'ringMod':
      return 'Ring Mod';
    case 'reverb':
      return 'Reverb';
    case 'delay':
      return 'Delay';
    case 'chorus':
      return 'Chorus';
    case 'flanger':
      return 'Flanger';
    case 'tremolo':
      return 'Tremolo';
    case 'pitchShift':
      return 'Pitch';
    case 'formantShift':
      return 'Formant';
    case 'timeStretch':
      return 'Time Stretch';
    case 'harmoniser':
      return 'Harmoniser';
    case 'autoTune':
      return 'Pitch Correct';
    case 'stutter':
      return 'Stutter';
    case 'glitch':
      return 'Glitch';
    case 'gainTrim':
      return 'Gain';
  }
}

export function effectKindCategory(kind: EffectKind): EffectCategory {
  switch (kind) {
    case 'eq':
    case 'highPass':
    case 'lowPass':
    case 'bandPass':
      return 'tone';
    case 'compressor':
    case 'limiter':
    case 'noiseGate':
      return 'dynamics';
    case 'distortion':
    case 'bitCrush':
    case 'sampleRateReduce':
    case 'ringMod':
      return 'colour';
    case 'reverb':
    case 'delay':
    case 'chorus':
    case 'flanger':
    case 'tremolo':
      return 'space';
    case 'pitchShift':
    case 'formantShift':
    case 'timeStretch':
    case 'harmoniser':
    case 'autoTune':
      return 'pitch';
    case 'stutter':
    case 'glitch':
      return 'rhythmic';
    case 'gainTrim':
      return 'utility';
  }
}

/** SF Symbol name, shared with the native build; the web icon layer maps it to an SVG. */
export function effectKindSymbolName(kind: EffectKind): string {
  switch (kind) {
    case 'eq':
      return 'slider.horizontal.3';
    case 'highPass':
      return 'waveform.path.badge.plus';
    case 'lowPass':
      return 'waveform.path.badge.minus';
    case 'bandPass':
      return 'waveform.path';
    case 'compressor':
      return 'arrow.down.right.and.arrow.up.left';
    case 'limiter':
      return 'arrow.up.to.line.compact';
    case 'noiseGate':
      return 'door.left.hand.closed';
    case 'distortion':
      return 'flame.fill';
    case 'bitCrush':
      return 'square.grid.3x3.fill';
    case 'sampleRateReduce':
      return 'chart.bar.fill';
    case 'ringMod':
      return 'circle.circle.fill';
    case 'reverb':
      return 'aqi.medium';
    case 'delay':
      return 'arrow.trianglehead.2.clockwise';
    case 'chorus':
      return 'person.3.fill';
    case 'flanger':
      return 'wind';
    case 'tremolo':
      return 'waveform.badge.magnifyingglass';
    case 'pitchShift':
      return 'arrow.up.arrow.down';
    case 'formantShift':
      return 'mouth.fill';
    case 'timeStretch':
      return 'arrow.left.and.right';
    case 'harmoniser':
      return 'music.note.list';
    case 'autoTune':
      return 'tuningfork';
    case 'stutter':
      return 'rectangle.split.3x1.fill';
    case 'glitch':
      return 'bolt.horizontal.fill';
    case 'gainTrim':
      return 'dial.medium.fill';
  }
}

/**
 * True when the effect can run in the live monitoring path while recording. Anything that
 * needs lookahead or a whole buffer is offline-only.
 */
export function effectKindSupportsLiveMonitoring(kind: EffectKind): boolean {
  return kind !== 'timeStretch' && kind !== 'autoTune' && kind !== 'glitch';
}

/**
 * True when the effect is implemented by rendering a new buffer rather than by a realtime
 * node, so it can only be applied to a finished clip.
 */
export function effectKindIsOfflineOnly(kind: EffectKind): boolean {
  return kind === 'timeStretch';
}

/**
 * The single parameter table. The UI generates its controls from these descriptors, the
 * DSP layer reads the same descriptors to configure nodes, the randomiser knows what it is
 * allowed to move, and the serialiser stores nothing but a `Record<string, number>`.
 */
export const EFFECT_PARAMETERS: Readonly<Record<EffectKind, readonly EffectParameter[]>> =
  Object.freeze({
    eq: [
      parameter('lowGain', 'Low', -24, 24, 0, 'dB'),
      parameter('lowFreq', 'Low Freq', 40, 500, 120, 'Hz', 'logarithmic'),
      parameter('midGain', 'Mid', -24, 24, 0, 'dB'),
      parameter('midFreq', 'Mid Freq', 200, 6000, 1000, 'Hz', 'logarithmic'),
      parameter('midQ', 'Mid Q', 0.2, 12, 1.0, '', 'logarithmic'),
      parameter('highGain', 'High', -24, 24, 0, 'dB'),
      parameter('highFreq', 'High Freq', 2000, 18000, 8000, 'Hz', 'logarithmic'),
    ],
    highPass: [
      parameter('frequency', 'Cutoff', 20, 12000, 120, 'Hz', 'logarithmic'),
      parameter('resonance', 'Resonance', -12, 24, 0, 'dB'),
    ],
    lowPass: [
      parameter('frequency', 'Cutoff', 80, 20000, 12000, 'Hz', 'logarithmic'),
      parameter('resonance', 'Resonance', -12, 24, 0, 'dB'),
    ],
    bandPass: [
      parameter('frequency', 'Centre', 80, 12000, 1200, 'Hz', 'logarithmic'),
      parameter('bandwidth', 'Width', 0.05, 5, 1.0, 'oct', 'logarithmic'),
    ],
    compressor: [
      parameter('threshold', 'Threshold', -40, 0, -18, 'dB'),
      parameter('ratio', 'Ratio', 1, 20, 4, ':1', 'logarithmic'),
      parameter('attack', 'Attack', 0.0005, 0.2, 0.005, 's', 'logarithmic'),
      parameter('release', 'Release', 0.01, 2.0, 0.15, 's', 'logarithmic'),
      parameter('makeup', 'Makeup', 0, 24, 0, 'dB'),
    ],
    limiter: [
      parameter('ceiling', 'Ceiling', -12, 0, -0.3, 'dB'),
      parameter('release', 'Release', 0.01, 1.0, 0.08, 's', 'logarithmic'),
      parameter('drive', 'Drive', 0, 18, 0, 'dB'),
    ],
    noiseGate: [
      parameter('threshold', 'Threshold', -80, -10, -48, 'dB'),
      parameter('attack', 'Attack', 0.0002, 0.05, 0.002, 's', 'logarithmic'),
      parameter('hold', 'Hold', 0, 0.5, 0.03, 's'),
      parameter('release', 'Release', 0.005, 1.0, 0.12, 's', 'logarithmic'),
    ],
    distortion: [
      parameter('drive', 'Drive', 0, 1, 0.35),
      parameter('tone', 'Tone', 0, 1, 0.5),
      parameter('character', 'Character', 0, 3, 0, '', 'stepped'),
      parameter('mix', 'Mix', 0, 1, 1.0),
    ],
    bitCrush: [
      parameter('bits', 'Bit Depth', 1, 16, 8, 'bit', 'stepped'),
      parameter('mix', 'Mix', 0, 1, 1.0),
    ],
    sampleRateReduce: [
      parameter('rate', 'Rate', 500, 44100, 8000, 'Hz', 'logarithmic'),
      parameter('mix', 'Mix', 0, 1, 1.0),
    ],
    ringMod: [
      parameter('frequency', 'Frequency', 10, 4000, 220, 'Hz', 'logarithmic'),
      parameter('mix', 'Mix', 0, 1, 0.6),
    ],
    reverb: [
      parameter('size', 'Size', 0, 1, 0.45),
      parameter('damping', 'Damping', 0, 1, 0.4),
      parameter('preDelay', 'Pre-Delay', 0, 0.2, 0.012, 's'),
      parameter('mix', 'Mix', 0, 1, 0.28),
    ],
    delay: [
      parameter('time', 'Time', 0.005, 2.0, 0.28, 's', 'logarithmic'),
      parameter('feedback', 'Feedback', 0, 0.95, 0.35),
      parameter('tone', 'Tone', 200, 18000, 6000, 'Hz', 'logarithmic'),
      parameter('sync', 'Tempo Sync', 0, 1, 0, '', 'toggle', false),
      parameter('mix', 'Mix', 0, 1, 0.3),
    ],
    chorus: [
      parameter('rate', 'Rate', 0.05, 8, 0.8, 'Hz', 'logarithmic'),
      parameter('depth', 'Depth', 0, 1, 0.4),
      parameter('voices', 'Voices', 1, 4, 2, '', 'stepped'),
      parameter('mix', 'Mix', 0, 1, 0.45),
    ],
    flanger: [
      parameter('rate', 'Rate', 0.02, 6, 0.25, 'Hz', 'logarithmic'),
      parameter('depth', 'Depth', 0, 1, 0.6),
      parameter('feedback', 'Feedback', -0.9, 0.9, 0.5),
      parameter('mix', 'Mix', 0, 1, 0.5),
    ],
    tremolo: [
      parameter('rate', 'Rate', 0.1, 24, 5, 'Hz', 'logarithmic'),
      parameter('depth', 'Depth', 0, 1, 0.6),
      parameter('shape', 'Shape', 0, 2, 0, '', 'stepped'),
    ],
    pitchShift: [
      parameter('semitones', 'Pitch', -24, 24, 0, 'st'),
      parameter('cents', 'Fine', -100, 100, 0, '¢'),
    ],
    formantShift: [parameter('shift', 'Formant', -12, 12, 0, 'st')],
    timeStretch: [parameter('rate', 'Speed', 0.25, 4.0, 1.0, '×', 'logarithmic')],
    harmoniser: [
      parameter('voice1', 'Voice 1', -12, 12, 4, 'st', 'stepped'),
      parameter('voice2', 'Voice 2', -12, 12, 7, 'st', 'stepped'),
      parameter('spread', 'Spread', 0, 1, 0.5),
      parameter('mix', 'Mix', 0, 1, 0.5),
    ],
    autoTune: [
      parameter('strength', 'Strength', 0, 1, 0.7),
      parameter('speed', 'Speed', 0, 1, 0.8),
      parameter('root', 'Key', 0, 11, 0, '', 'stepped'),
      parameter('scale', 'Scale', 0, 2, 0, '', 'stepped'),
    ],
    stutter: [
      parameter('division', 'Division', 0, 5, 3, '', 'stepped'),
      parameter('gateLength', 'Gate', 0.05, 1, 0.6),
      parameter('mix', 'Mix', 0, 1, 1.0),
    ],
    glitch: [
      parameter('density', 'Density', 0, 1, 0.4),
      parameter('sliceSize', 'Slice', 0.01, 0.5, 0.08, 's', 'logarithmic'),
      parameter('reverseChance', 'Reverse', 0, 1, 0.3),
      parameter('pitchChance', 'Pitch Jump', 0, 1, 0.25),
      parameter('seed', 'Seed', 0, 9999, 1, '', 'stepped', false),
    ],
    gainTrim: [parameter('gain', 'Gain', -30, 18, 0, 'dB')],
  });

export function effectKindParameters(kind: EffectKind): readonly EffectParameter[] {
  return EFFECT_PARAMETERS[kind];
}

export function effectKindParameter(kind: EffectKind, key: string): EffectParameter | undefined {
  return EFFECT_PARAMETERS[kind].find((entry) => entry.key === key);
}

export function effectKindDefaultValues(kind: EffectKind): Record<string, number> {
  const values: Record<string, number> = {};
  for (const descriptor of EFFECT_PARAMETERS[kind]) values[descriptor.key] = descriptor.defaultValue;
  return values;
}

// MARK: - Serialised nodes and chains

export interface EffectNode {
  id: UUID;
  kind: EffectKind;
  values: Record<string, number>;
  isBypassed: boolean;
}

export interface MakeEffectNodeOptions {
  values?: Record<string, number>;
  isBypassed?: boolean;
  id?: UUID;
}

export function makeEffectNode(kind: EffectKind, options: MakeEffectNodeOptions = {}): EffectNode {
  return {
    id: options.id ?? newUUID(),
    kind,
    values: options.values ?? effectKindDefaultValues(kind),
    isBypassed: options.isBypassed ?? false,
  };
}

/**
 * Reads a parameter, falling back to its descriptor default and always clamped, so a
 * hand-edited or migrated project can never push a node out of range.
 */
export function effectNodeValue(node: EffectNode, key: string): number {
  const descriptor = effectKindParameter(node.kind, key);
  if (descriptor === undefined) return node.values[key] ?? 0;
  return effectParameterClamp(descriptor, node.values[key] ?? descriptor.defaultValue);
}

export function effectNodeSetValue(node: EffectNode, key: string, value: number): void {
  const descriptor = effectKindParameter(node.kind, key);
  if (descriptor === undefined) return;
  node.values[key] = effectParameterClamp(descriptor, value);
}

/** Fills in any parameter added by a newer schema version and drops any it retired. */
export function normaliseEffectNode(node: EffectNode): void {
  for (const descriptor of EFFECT_PARAMETERS[node.kind]) {
    const existing = node.values[descriptor.key];
    node.values[descriptor.key] =
      existing === undefined ? descriptor.defaultValue : effectParameterClamp(descriptor, existing);
  }
  for (const key of Object.keys(node.values)) {
    if (effectKindParameter(node.kind, key) === undefined) delete node.values[key];
  }
}

export interface EffectChain {
  nodes: EffectNode[];
  /** Master bypass for the whole chain — drives the A/B compare control. */
  isBypassed: boolean;
}

export function makeEffectChain(nodes: EffectNode[] = [], isBypassed = false): EffectChain {
  return { nodes, isBypassed };
}

export function emptyEffectChain(): EffectChain {
  return { nodes: [], isBypassed: false };
}

export function effectChainActiveNodes(chain: EffectChain): EffectNode[] {
  return chain.isBypassed ? [] : chain.nodes.filter((node) => !node.isBypassed);
}

export function effectChainIsEmpty(chain: EffectChain): boolean {
  return chain.nodes.length === 0;
}

/** True when the chain can be reproduced by realtime nodes in the monitoring path. */
export function effectChainSupportsLiveMonitoring(chain: EffectChain): boolean {
  return effectChainActiveNodes(chain).every((node) => effectKindSupportsLiveMonitoring(node.kind));
}

export function effectChainRequiresOfflineRender(chain: EffectChain): boolean {
  return effectChainActiveNodes(chain).some((node) => effectKindIsOfflineOnly(node.kind));
}

export function normaliseEffectChain(chain: EffectChain): void {
  for (const node of chain.nodes) normaliseEffectNode(node);
}

export function effectChainNode(chain: EffectChain, id: UUID): EffectNode | undefined {
  return chain.nodes.find((node) => node.id === id);
}

export function effectChainUpsert(chain: EffectChain, node: EffectNode): void {
  const index = chain.nodes.findIndex((entry) => entry.id === node.id);
  if (index >= 0) chain.nodes[index] = node;
  else chain.nodes.push(node);
}

// MARK: - Presets and macros

export type EffectPresetFlavour =
  | 'subtle'
  | 'strange'
  | 'robotic'
  | 'cinematic'
  | 'retro'
  | 'rhythmic'
  | 'ambient'
  | 'aggressive'
  | 'comedic';

export const EFFECT_PRESET_FLAVOURS: readonly EffectPresetFlavour[] = [
  'subtle',
  'strange',
  'robotic',
  'cinematic',
  'retro',
  'rhythmic',
  'ambient',
  'aggressive',
  'comedic',
];

export function effectPresetFlavourDisplayName(flavour: EffectPresetFlavour): string {
  return flavour.charAt(0).toUpperCase() + flavour.slice(1);
}

export interface EffectPreset {
  id: UUID;
  name: string;
  flavour: EffectPresetFlavour;
  chain: EffectChain;
  /** Presets shipped with the app cannot be deleted, only duplicated. */
  isBuiltIn: boolean;
}

export function makeEffectPreset(
  name: string,
  flavour: EffectPresetFlavour,
  chain: EffectChain,
  options: { id?: UUID; isBuiltIn?: boolean } = {},
): EffectPreset {
  return {
    id: options.id ?? newUUID(),
    name,
    flavour,
    chain,
    isBuiltIn: options.isBuiltIn ?? false,
  };
}

/**
 * A macro maps one 0…1 control onto many parameters at once, which is how the Sound Lab's
 * X/Y pad and the "one big knob" simple mode are built.
 */
export interface MacroBinding {
  id: UUID;
  nodeID: UUID;
  parameterKey: string;
  /** Value when the macro sits at 0. */
  fromValue: number;
  /** Value when the macro sits at 1. */
  toValue: number;
}

export function makeMacroBinding(
  nodeID: UUID,
  parameterKey: string,
  fromValue: number,
  toValue: number,
  id: UUID = newUUID(),
): MacroBinding {
  return { id, nodeID, parameterKey, fromValue, toValue };
}

export function macroBindingValue(binding: MacroBinding, position: number): number {
  const p = clampFinite(position, 0, 1, 0);
  return binding.fromValue + (binding.toValue - binding.fromValue) * p;
}

export interface Macro {
  id: UUID;
  name: string;
  position: number;
  bindings: MacroBinding[];
}

export function makeMacro(
  name: string,
  options: { id?: UUID; position?: number; bindings?: MacroBinding[] } = {},
): Macro {
  return {
    id: options.id ?? newUUID(),
    name,
    position: options.position ?? 0,
    bindings: options.bindings ?? [],
  };
}

// MARK: - Fades and envelopes

export type FadeCurve = 'linear' | 'equalPower' | 'exponential' | 'logarithmic';

export const FADE_CURVES: readonly FadeCurve[] = [
  'linear',
  'equalPower',
  'exponential',
  'logarithmic',
];

export function fadeCurveDisplayName(curve: FadeCurve): string {
  switch (curve) {
    case 'linear':
      return 'Linear';
    case 'equalPower':
      return 'Equal Power';
    case 'exponential':
      return 'Exponential';
    case 'logarithmic':
      return 'Logarithmic';
  }
}

/** Gain multiplier at normalised position `t` (0 = start of fade-in, 1 = full level). */
export function fadeCurveGain(curve: FadeCurve, t: number): number {
  const p = clampFinite(t, 0, 1, 0);
  switch (curve) {
    case 'linear':
      return p;
    case 'equalPower':
      return Math.sin((p * Math.PI) / 2);
    case 'exponential':
      return p * p;
    case 'logarithmic':
      return Math.sqrt(p);
  }
}

export interface AmpEnvelope {
  attack: TimeInterval;
  decay: TimeInterval;
  sustain: number;
  release: TimeInterval;
}

export function makeAmpEnvelope(options: Partial<AmpEnvelope> = {}): AmpEnvelope {
  return {
    attack: options.attack ?? 0.001,
    decay: options.decay ?? 0.08,
    sustain: options.sustain ?? 1.0,
    release: options.release ?? 0.06,
  };
}

export function instantAmpEnvelope(): AmpEnvelope {
  return { attack: 0.0005, decay: 0, sustain: 1, release: 0.008 };
}

/**
 * `ClosedRange<TimeInterval>` has no stable encoding across schema versions, so PadLab
 * stores explicit bounds.
 */
export interface ClosedRangeCodable {
  lowerBound: TimeInterval;
  upperBound: TimeInterval;
}

export function makeClosedRange(lowerBound: number, upperBound: number): ClosedRangeCodable {
  return {
    lowerBound: Math.min(lowerBound, upperBound),
    upperBound: Math.max(lowerBound, upperBound),
  };
}

export function closedRangeLength(range: ClosedRangeCodable): number {
  return range.upperBound - range.lowerBound;
}

export function closedRangeContains(range: ClosedRangeCodable, value: number): boolean {
  return value >= range.lowerBound && value <= range.upperBound;
}

// MARK: - Non-destructive edit

/**
 * The complete non-destructive description of how a source file should sound. Applying
 * `sampleEditIdentity()` to a file must reproduce the file bit-for-bit.
 */
export interface SampleEdit {
  /** Playable region in seconds, measured against the source file. */
  startTime: TimeInterval;
  /** Absent means "to the end of the file". */
  endTime?: TimeInterval;

  gainDB: number;
  /** -1 left … +1 right. */
  pan: number;
  pitchSemitones: number;
  pitchCents: number;
  /** 0.25 … 4.0. Affects duration. */
  playbackRate: number;
  /** When true, rate changes do not change pitch. */
  preservesPitchWhenStretched: boolean;

  isReversed: boolean;
  isNormalised: boolean;
  normalisationTargetDB: number;

  fadeInDuration: TimeInterval;
  fadeOutDuration: TimeInterval;
  fadeCurve: FadeCurve;

  loopEnabled: boolean;
  /** Loop points in source-file seconds. Clamped into the region. */
  loopStart?: TimeInterval;
  loopEnd?: TimeInterval;
  loopCrossfade: TimeInterval;

  envelope?: AmpEnvelope;

  chain: EffectChain;

  /** Regions the user silenced, in source-file seconds. */
  silencedRegions: ClosedRangeCodable[];
}

/** A fresh identity edit. Returns a new object every call — never share a mutable model. */
export function sampleEditIdentity(): SampleEdit {
  return {
    startTime: 0,
    gainDB: 0,
    pan: 0,
    pitchSemitones: 0,
    pitchCents: 0,
    playbackRate: 1,
    preservesPitchWhenStretched: false,
    isReversed: false,
    isNormalised: false,
    normalisationTargetDB: -1.0,
    fadeInDuration: 0,
    fadeOutDuration: 0,
    fadeCurve: 'equalPower',
    loopEnabled: false,
    loopCrossfade: 0,
    chain: emptyEffectChain(),
    silencedRegions: [],
  };
}

export function makeSampleEdit(options: Partial<SampleEdit> = {}): SampleEdit {
  return { ...sampleEditIdentity(), ...options };
}

/**
 * True when playback can read the decoded buffer directly with no rendering pass — which
 * is what lets a pad be triggered with zero preparation.
 */
export function isSampleEditIdentity(edit: SampleEdit): boolean {
  return (
    edit.startTime === 0 &&
    edit.endTime === undefined &&
    edit.gainDB === 0 &&
    edit.pan === 0 &&
    edit.pitchSemitones === 0 &&
    edit.pitchCents === 0 &&
    edit.playbackRate === 1 &&
    edit.preservesPitchWhenStretched === false &&
    edit.isReversed === false &&
    edit.isNormalised === false &&
    edit.normalisationTargetDB === -1.0 &&
    edit.fadeInDuration === 0 &&
    edit.fadeOutDuration === 0 &&
    edit.fadeCurve === 'equalPower' &&
    edit.loopEnabled === false &&
    edit.loopStart === undefined &&
    edit.loopEnd === undefined &&
    edit.loopCrossfade === 0 &&
    edit.envelope === undefined &&
    edit.chain.nodes.length === 0 &&
    edit.chain.isBypassed === false &&
    edit.silencedRegions.length === 0
  );
}

/** Total pitch offset in cents, which is what a time-pitch unit actually wants. */
export function sampleEditTotalPitchCents(edit: SampleEdit): number {
  return edit.pitchSemitones * 100 + edit.pitchCents;
}

/** Sounding length in seconds given a source duration. */
export function sampleEditSoundingDuration(edit: SampleEdit, sourceDuration: TimeInterval): number {
  const end = Math.min(edit.endTime ?? sourceDuration, sourceDuration);
  const raw = Math.max(0, end - Math.max(0, edit.startTime));
  return edit.playbackRate > 0 ? raw / edit.playbackRate : raw;
}

/**
 * Clamps every value into a valid range for the given source duration. Called after load
 * and after migration, so a corrupt project can never crash the engine. Mutates in place.
 */
export function clampSampleEdit(edit: SampleEdit, sourceDuration: TimeInterval): SampleEdit {
  const safeDuration = Math.max(0, clampFinite(sourceDuration, 0, Number.MAX_SAFE_INTEGER, 0));
  edit.startTime = clampFinite(edit.startTime, 0, safeDuration, 0);
  if (edit.endTime !== undefined) {
    edit.endTime = clampFinite(edit.endTime, edit.startTime, safeDuration, safeDuration);
  }
  const regionEnd = edit.endTime ?? safeDuration;
  edit.gainDB = clampFinite(edit.gainDB, -60, 24, 0);
  edit.pan = clampFinite(edit.pan, -1, 1, 0);
  edit.pitchSemitones = clampFinite(edit.pitchSemitones, -24, 24, 0);
  edit.pitchCents = clampFinite(edit.pitchCents, -100, 100, 0);
  edit.playbackRate = clampFinite(edit.playbackRate, 0.25, 4, 1);
  edit.normalisationTargetDB = clampFinite(edit.normalisationTargetDB, -60, 0, -1);

  const regionLength = Math.max(0, regionEnd - edit.startTime);
  edit.fadeInDuration = clampFinite(edit.fadeInDuration, 0, regionLength, 0);
  edit.fadeOutDuration = clampFinite(
    edit.fadeOutDuration,
    0,
    Math.max(0, regionLength - edit.fadeInDuration),
    0,
  );

  if (edit.loopEnabled) {
    const loopStart = clampFinite(edit.loopStart ?? edit.startTime, edit.startTime, regionEnd, edit.startTime);
    const loopEnd = clampFinite(edit.loopEnd ?? regionEnd, loopStart, regionEnd, regionEnd);
    edit.loopStart = loopStart;
    edit.loopEnd = loopEnd;
    edit.loopCrossfade = clampFinite(edit.loopCrossfade, 0, Math.max(0, (loopEnd - loopStart) / 2), 0);
  }

  if (edit.envelope !== undefined) {
    const envelope = edit.envelope;
    envelope.attack = clampFinite(envelope.attack, 0, 5, 0.001);
    envelope.decay = clampFinite(envelope.decay, 0, 5, 0.08);
    envelope.sustain = clampFinite(envelope.sustain, 0, 1, 1);
    envelope.release = clampFinite(envelope.release, 0, 5, 0.06);
  }

  const regions: ClosedRangeCodable[] = [];
  for (const region of edit.silencedRegions) {
    const lower = clampFinite(region.lowerBound, edit.startTime, regionEnd, edit.startTime);
    const upper = clampFinite(region.upperBound, lower, regionEnd, lower);
    if (upper > lower) regions.push({ lowerBound: lower, upperBound: upper });
  }
  edit.silencedRegions = regions;

  normaliseEffectChain(edit.chain);
  return edit;
}

// MARK: - Sample assets

export interface SampleLicence {
  name: string;
  url?: string;
  attribution?: string;
  allowsRedistribution: boolean;
}

export function ccZeroLicence(): SampleLicence {
  return {
    name: 'CC0 1.0 Universal',
    url: 'https://creativecommons.org/publicdomain/zero/1.0/',
    allowsRedistribution: true,
  };
}

/** Anything the user made themselves. */
export function userOwnedLicence(): SampleLicence {
  return { name: 'Created by you', allowsRedistribution: true };
}

/**
 * An immutable reference to a decoded-once source file living in the project's media
 * folder. Nothing in PadLab ever writes back over a source file — edits live in
 * `SampleEdit` and are rendered only on bounce or export.
 */
export interface SampleAsset {
  id: UUID;
  /** Display name, user-editable, independent of the stored file. */
  name: string;
  /** File name inside the project's media folder. Never a path, so packages stay portable. */
  fileName: string;
  /** The name the file had when it arrived, kept for the library and error messages. */
  originalFileName: string;
  origin: SampleOrigin;
  createdAt: ISODate;
  duration: TimeInterval;
  sampleRate: number;
  channelCount: number;
  frameCount: number;
  byteSize: number;
  /** SHA-256 of the source file, for corruption detection and import de-duplication. */
  contentHash: string;
  /** Edits the editor last saved. Pads copy this on assignment, then diverge. */
  defaultEdit: SampleEdit;
  tags: string[];
  isFavourite: boolean;
  /** Set when the sample came from a pack whose licence restricts redistribution. */
  licence?: SampleLicence;
  /** Text description, used by VoiceOver for bundled content with no obvious name. */
  caption?: string;
  /** Detected tempo, if the analyser found a confident answer. */
  detectedBPM?: number;
  /** Slice markers in seconds from the start of the source file. */
  sliceMarkers: TimeInterval[];
}

export interface MakeSampleAssetOptions {
  id?: UUID;
  name: string;
  fileName: string;
  originalFileName: string;
  origin: SampleOrigin;
  createdAt?: ISODate;
  duration: TimeInterval;
  sampleRate: number;
  channelCount: number;
  frameCount: number;
  byteSize: number;
  contentHash: string;
  defaultEdit?: SampleEdit;
  tags?: string[];
  isFavourite?: boolean;
  licence?: SampleLicence;
  caption?: string;
  detectedBPM?: number;
  sliceMarkers?: TimeInterval[];
}

export function makeSampleAsset(options: MakeSampleAssetOptions): SampleAsset {
  const asset: SampleAsset = {
    id: options.id ?? newUUID(),
    name: options.name,
    fileName: options.fileName,
    originalFileName: options.originalFileName,
    origin: options.origin,
    createdAt: options.createdAt ?? nowISODate(),
    duration: options.duration,
    sampleRate: options.sampleRate,
    channelCount: options.channelCount,
    frameCount: options.frameCount,
    byteSize: options.byteSize,
    contentHash: options.contentHash,
    defaultEdit: options.defaultEdit ?? sampleEditIdentity(),
    tags: options.tags ?? [],
    isFavourite: options.isFavourite ?? false,
    sliceMarkers: options.sliceMarkers ?? [],
  };
  if (options.licence !== undefined) asset.licence = options.licence;
  if (options.caption !== undefined) asset.caption = options.caption;
  if (options.detectedBPM !== undefined) asset.detectedBPM = options.detectedBPM;
  return asset;
}

// MARK: - Loops
//
// A loop is a bed, not a step. It is a whole bar or more of music that *spans* a pattern
// instead of sitting inside one of its cells, and that difference decides how it is
// assigned to a pad, how it behaves on a step grid, and whether slicing it is offered.
//
// Everything in PadLab that needs to know "is this a loop?" asks `assetIsLoop`. One test,
// one answer: the board, the library, the sequencer screen, both schedulers and the slicer
// all read the same function, so a sound cannot be a loop in one screen and a one-shot in
// the next.

/**
 * The tag the sound library writes on every loop it ships, and the explicit signal the loop
 * test prefers over any measurement.
 *
 * `audio/library/loops.ts` stamps it on the catalogue entry; imports and recordings can be
 * given it by hand from the library screen. It survives the project round trip to iOS
 * because tags are part of the shared model.
 */
export const LOOP_TAG = 'loop';

/**
 * Why a sound counts as a loop.
 *
 * `tag` and `tempo` are explicit — someone or something declared it. `length` is the
 * fallback measurement and is the weakest of the three, which is why it is reported
 * separately: the UI can say "this is longer than a bar" rather than claiming the sound
 * announced itself.
 */
export type LoopEvidence = 'tag' | 'tempo' | 'length';

/** Seconds in one bar at a given tempo and time signature. */
export function barSeconds(tempo: number, beatsPerBar: number): number {
  const safeTempo = clampFinite(tempo, 20, 300, 96);
  const beats = Math.max(1, Math.round(clampFinite(beatsPerBar, 1, 32, 4)));
  return (60 / safeTempo) * beats;
}

/**
 * Why this asset is a loop, or `null` when it is a one-shot.
 *
 * The order is deliberate. An explicit signal beats a measurement, because a measurement
 * has to guess: a long crash cymbal and a one-bar break are the same length. The length
 * check is a *secondary* test and is set at a full bar rather than at a step — a 300 ms kick
 * covers two steps at 90 BPM and is still obviously a one-shot, so a step-relative threshold
 * would call most of a drum kit a loop.
 */
export function assetLoopEvidence(
  asset: SampleAsset,
  tempo: number,
  beatsPerBar: number,
): LoopEvidence | null {
  if (asset.tags.some((tag) => tag.trim().toLowerCase() === LOOP_TAG)) return 'tag';
  if (asset.detectedBPM !== undefined && asset.detectedBPM > 0) return 'tempo';
  // A whole bar plus a little: a sample a few milliseconds over the line is a one-shot that
  // happens to ring out, not a bar of music.
  const bar = barSeconds(tempo, beatsPerBar);
  return asset.duration >= bar * 1.05 ? 'length' : null;
}

export function assetIsLoop(asset: SampleAsset, tempo: number, beatsPerBar: number): boolean {
  return assetLoopEvidence(asset, tempo, beatsPerBar) !== null;
}

/** The same test, reading the tempo and time signature from the project the asset lives in. */
export function projectAssetIsLoop(project: Project, asset: SampleAsset | undefined): boolean {
  if (asset === undefined) return false;
  return assetIsLoop(asset, project.tempo, project.timeSignatureBeats);
}

/** Every loop in the project, newest first — what the slicer and the loop pickers list. */
export function projectLoopAssets(project: Project): SampleAsset[] {
  return project.assets
    .filter((asset) => projectAssetIsLoop(project, asset))
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/**
 * True when this pad is playing a loop — the test the sequencer uses to decide whether a
 * track is a loop track.
 */
export function projectPadPlaysLoop(project: Project, pad: Pad): boolean {
  if (pad.assetID === undefined) return false;
  return projectAssetIsLoop(project, projectAsset(project, pad.assetID));
}

/**
 * Playback rate that makes a loop line up with the project. Above 1 the loop plays faster.
 *
 * Returns 1 when there is nothing to fit to, so a caller can apply it unconditionally. The
 * range matches `SampleEdit.playbackRate`'s own clamp — beyond ±2 octaves a "fit" is a
 * different sound, not a tempo match.
 */
export function loopFitRate(asset: SampleAsset, tempo: number): number {
  const detected = asset.detectedBPM;
  if (detected === undefined || detected <= 0) return 1;
  return clamp(tempo / detected, 0.25, 4);
}

/** True when fitting this loop would actually change how it plays. */
export function loopNeedsTempoFit(asset: SampleAsset, tempo: number, currentRate: number): boolean {
  if (asset.detectedBPM === undefined || asset.detectedBPM <= 0) return false;
  return Math.abs(loopFitRate(asset, tempo) - currentRate) > 0.005;
}

/**
 * How many bars of the project's grid the sound covers at `rate`.
 *
 * Measured against the project rather than against the loop's own tempo, because the
 * question this answers is always "how much of *this* pattern does it fill?".
 */
export function loopBarLength(
  asset: SampleAsset,
  tempo: number,
  beatsPerBar: number,
  rate = 1,
): number {
  const bar = barSeconds(tempo, beatsPerBar);
  const safeRate = clampFinite(rate, 0.25, 4, 1);
  return bar <= 0 ? 0 : asset.duration / safeRate / bar;
}

/**
 * `Date.toISOString()` emits milliseconds, which Swift's `.iso8601` decoding strategy
 * rejects outright. Everything that stamps a model date goes through here.
 */
export function nowISODate(): ISODate {
  return toISODate(new Date());
}

export function toISODate(date: Date): ISODate {
  return `${date.toISOString().slice(0, 19)}Z`;
}

// MARK: - Pad

export interface Pad {
  id: UUID;
  /** Index inside its bank. Reordering rewrites these so they stay dense and sorted. */
  slot: number;
  name: string;
  assetID?: UUID;
  edit: SampleEdit;

  playbackMode: PlaybackMode;
  retrigger: RetriggerBehaviour;
  quantize: Quantize;
  /** Pads sharing a defined choke group cut each other off. */
  chokeGroup?: number;
  /** Release fade applied when a `gate` pad is let go, in seconds. */
  releaseFade: TimeInterval;

  /** 0…1, tapered to dB by the engine. */
  volume: number;
  /** -1…1. */
  pan: number;
  isMuted: boolean;
  isSoloed: boolean;

  color: PadColor;
  symbolName?: string;
  artworkFileName?: string;
  category?: string;
  tags: string[];
  isFavourite: boolean;

  /** Maximum simultaneous voices for `layer` retrigger — a stuck finger cannot exhaust the pool. */
  polyphony: number;
}

export function makePad(slot: number, options: Partial<Omit<Pad, 'slot'>> = {}): Pad {
  const pad: Pad = {
    id: options.id ?? newUUID(),
    slot,
    name: options.name ?? '',
    edit: options.edit ?? sampleEditIdentity(),
    playbackMode: options.playbackMode ?? 'oneShot',
    retrigger: options.retrigger ?? 'restart',
    quantize: options.quantize ?? 'off',
    releaseFade: options.releaseFade ?? 0.08,
    volume: options.volume ?? 0.8,
    pan: options.pan ?? 0,
    isMuted: options.isMuted ?? false,
    isSoloed: options.isSoloed ?? false,
    color: options.color ?? 'magenta',
    tags: options.tags ?? [],
    isFavourite: options.isFavourite ?? false,
    polyphony: options.polyphony ?? 4,
  };
  if (options.assetID !== undefined) pad.assetID = options.assetID;
  if (options.chokeGroup !== undefined) pad.chokeGroup = options.chokeGroup;
  if (options.symbolName !== undefined) pad.symbolName = options.symbolName;
  if (options.artworkFileName !== undefined) pad.artworkFileName = options.artworkFileName;
  if (options.category !== undefined) pad.category = options.category;
  return pad;
}

export function padIsEmpty(pad: Pad): boolean {
  return pad.assetID === undefined || pad.assetID === null;
}

/** What the tile shows when it is not being touched and not sounding. */
export function padRestingLabel(pad: Pad): string {
  if (pad.name !== '') return pad.name;
  return padIsEmpty(pad) ? '' : 'Untitled';
}

// MARK: - Bank

export interface Bank {
  id: UUID;
  name: string;
  grid: GridSize;
  pads: Pad[];
  color: PadColor;
}

export function makeBank(
  name: string,
  options: { id?: UUID; grid?: GridSize; pads?: Pad[]; color?: PadColor } = {},
): Bank {
  const grid = options.grid ?? 'g3x4';
  const pads =
    options.pads ?? Array.from({ length: gridCapacity(grid) }, (_unused, slot) => makePad(slot));
  return {
    id: options.id ?? newUUID(),
    name,
    grid,
    pads,
    color: options.color ?? 'violet',
  };
}

export function bankPadAt(bank: Bank, slot: number): Pad | undefined {
  return bank.pads.find((pad) => pad.slot === slot);
}

export function bankLoadedPadCount(bank: Bank): number {
  return bank.pads.filter((pad) => !padIsEmpty(pad)).length;
}

/**
 * Grows or shrinks the pad array to match a new grid. Pads are never destroyed by a
 * shrink — they are kept beyond the visible capacity so switching back restores them.
 */
export function resizeBank(bank: Bank, grid: GridSize): void {
  bank.grid = grid;
  const capacity = gridCapacity(grid);
  for (let slot = bank.pads.length; slot < capacity; slot++) bank.pads.push(makePad(slot));
}

/** Pads visible for the current grid, in slot order. */
export function bankVisiblePads(bank: Bank): Pad[] {
  return [...bank.pads].sort((a, b) => a.slot - b.slot).slice(0, gridCapacity(bank.grid));
}

// MARK: - Mixer

export interface MasterSettings {
  volume: number;
  /** Extra headroom applied before the limiter so stacked pads do not clip. */
  headroomDB: number;
  limiterEnabled: boolean;
  limiterCeilingDB: number;
  chain: EffectChain;
}

/**
 * Defaults chosen for a phone speaker, not for a mixing desk.
 *
 * These used to stack three separate trims: a fader at 0.85, three more decibels of headroom,
 * and a limiter half a decibel below full scale. Together that is about five decibels given
 * away before a single sound is played, which on a phone at maximum volume is roughly half the
 * loudness the hardware can produce. On a studio monitor that caution is free. On the speaker
 * strip of an iPhone it is the difference between an instrument and a toy.
 *
 * So the fader starts at unity and the pre-limiter trim is gone. Protection against stacked
 * pads clipping comes from the limiter, which is on by default and is the right tool for it —
 * turning everything down in advance is not headroom management, it is just quieter.
 *
 * The ceiling stays a little under 0 dBFS. Inter-sample peaks can exceed the sample values a
 * limiter sees, and a converter asked for more than full scale distorts audibly.
 */
export function makeMasterSettings(options: Partial<MasterSettings> = {}): MasterSettings {
  return {
    volume: options.volume ?? 1.0,
    headroomDB: options.headroomDB ?? 0,
    limiterEnabled: options.limiterEnabled ?? true,
    limiterCeilingDB: options.limiterCeilingDB ?? -0.3,
    chain: options.chain ?? emptyEffectChain(),
  };
}

/**
 * Lifts a board that was made under the old quiet defaults.
 *
 * Defaults only apply to new projects, so without this every board anyone already made would
 * stay five decibels down for ever, and the fix would look like it had not worked. Only the
 * exact old default values are moved: anyone who deliberately set a level keeps it.
 */
export function liftLegacyMasterLevels(master: MasterSettings): void {
  if (master.volume === 0.85) master.volume = 1.0;
  if (master.headroomDB === -3.0) master.headroomDB = 0;
  if (master.limiterCeilingDB === -0.5) master.limiterCeilingDB = -0.3;
}

export interface MetronomeSettings {
  isEnabled: boolean;
  volume: number;
  countInBars: number;
  accentFirstBeat: boolean;
}

export function makeMetronomeSettings(options: Partial<MetronomeSettings> = {}): MetronomeSettings {
  return {
    isEnabled: options.isEnabled ?? false,
    volume: options.volume ?? 0.6,
    countInBars: options.countInBars ?? 1,
    accentFirstBeat: options.accentFirstBeat ?? true,
  };
}

// MARK: - Sequencer

/**
 * One programmed hit. A step is "on" when `isActive`; everything else describes how the hit
 * should sound. Probability and ratchets are evaluated at schedule time, never on the audio
 * render thread.
 */
export interface Step {
  isActive: boolean;
  /** 0…1, maps to a dB trim on the voice. */
  velocity: number;
  /** 0…1 chance the step fires on any given pass. */
  probability: number;
  /** Semitone offset applied on top of the pad's own pitch. */
  pitchOffset: number;
  /** Micro-timing nudge as a fraction of one step, -0.5…+0.5. */
  microTiming: number;
  /** Number of retriggers packed into this step. 1 = a single hit. */
  ratchets: number;
  /** Per-step choke: when true, this hit cuts the track's previous voice. */
  chokesPrevious: boolean;
}

export function makeStep(options: Partial<Step> = {}): Step {
  return {
    isActive: options.isActive ?? false,
    velocity: options.velocity ?? 0.85,
    probability: options.probability ?? 1.0,
    pitchOffset: options.pitchOffset ?? 0,
    microTiming: options.microTiming ?? 0,
    ratchets: options.ratchets ?? 1,
    chokesPrevious: options.chokesPrevious ?? false,
  };
}

export function offStep(): Step {
  return makeStep();
}

export function sanitiseStep(step: Step): void {
  step.velocity = clampFinite(step.velocity, 0, 1, 0.85);
  step.probability = clampFinite(step.probability, 0, 1, 1);
  step.pitchOffset = clampFinite(step.pitchOffset, -24, 24, 0);
  step.microTiming = clampFinite(step.microTiming, -0.5, 0.5, 0);
  step.ratchets = Math.round(clampFinite(step.ratchets, 1, 8, 1));
}

/**
 * True when the step differs from a plain active hit — drives the "edited step" marker on
 * the grid, so per-step detail is visible without opening an inspector.
 */
export function stepHasDetail(step: Step): boolean {
  return (
    step.probability < 0.999 ||
    step.pitchOffset !== 0 ||
    Math.abs(step.microTiming) > 0.001 ||
    step.ratchets > 1
  );
}

export const PATTERN_LENGTH_OPTIONS: readonly number[] = [8, 16, 32, 64];
export const PATTERN_LENGTH_MAXIMUM = 64;
export const PATTERN_LENGTH_DEFAULT = 16;

/** One row of the sequencer, bound to a pad. */
export interface Track {
  id: UUID;
  padID?: UUID;
  name: string;
  steps: Step[];
  /** Tracks can be shorter than the pattern for polyrhythms. Always ≤ `steps.length`. */
  length: number;
  isMuted: boolean;
  isSoloed: boolean;
  color: PadColor;
  /**
   * Fires at most once per pass, at the earliest active step — the track mode a loop needs.
   *
   * A loop spans the pattern instead of sitting in a step, so two lit steps mean two copies
   * of the same bar playing against each other half a bar apart. This mode is what makes a
   * loop on a grid a deliberate thing rather than a silent mistake: the bed restarts once
   * per pass, from wherever its single hit sits.
   *
   * Optional so it is absent from tracks written before it existed and from every pattern
   * the iPhone app has ever saved — an omitted key decodes as "off", which is what a
   * one-shot track has always done.
   */
  oneHitPerPass?: boolean;
}

export function makeTrack(options: Partial<Track> = {}): Track {
  const length = options.length ?? 16;
  const track: Track = {
    id: options.id ?? newUUID(),
    name: options.name ?? '',
    steps: options.steps ?? Array.from({ length: Math.max(length, 16) }, () => offStep()),
    length,
    isMuted: options.isMuted ?? false,
    isSoloed: options.isSoloed ?? false,
    color: options.color ?? 'magenta',
  };
  if (options.padID !== undefined) track.padID = options.padID;
  if (options.oneHitPerPass === true) track.oneHitPerPass = true;
  return track;
}

/**
 * Index of the step a one-hit-per-pass track actually fires on, or -1 when it is silent.
 *
 * Both schedulers and the grid read this, so what is drawn as the live step and what is
 * heard are the same step by construction.
 */
export function trackLeadingStepIndex(track: Track): number {
  const span = Math.max(1, Math.min(track.length, track.steps.length));
  for (let index = 0; index < span; index++) {
    if (track.steps[index]?.isActive === true) return index;
  }
  return -1;
}

/**
 * True when this step is scheduled to sound. Identical to `step.isActive` for an ordinary
 * track; on a loop track only the leading step passes.
 */
export function trackStepSounds(track: Track, index: number): boolean {
  if (track.steps[index]?.isActive !== true) return false;
  if (track.oneHitPerPass !== true) return true;
  return trackLeadingStepIndex(track) === index;
}

export function trackActiveStepCount(track: Track): number {
  return track.steps.slice(0, track.length).filter((step) => step.isActive).length;
}

export function trackSetLength(track: Track, length: number): void {
  track.length = Math.round(clampFinite(length, 1, PATTERN_LENGTH_MAXIMUM, PATTERN_LENGTH_DEFAULT));
  while (track.steps.length < track.length) track.steps.push(offStep());
}

export function clearTrack(track: Track): void {
  for (let index = 0; index < track.steps.length; index++) track.steps[index] = offStep();
}

export function sanitiseTrack(track: Track): void {
  track.length = Math.round(
    clampFinite(track.length, 1, PATTERN_LENGTH_MAXIMUM, PATTERN_LENGTH_DEFAULT),
  );
  if (!Array.isArray(track.steps)) track.steps = [];
  while (track.steps.length < track.length) track.steps.push(offStep());
  for (const step of track.steps) sanitiseStep(step);
  // Anything that is not the literal `true` is off, and is removed rather than written back
  // as `false`: the key is absent on every track the iPhone app writes, and a project that
  // round-trips must not grow a field on each pass through the web build.
  if (track.oneHitPerPass !== true) delete track.oneHitPerPass;
}

/** Step that should fire on absolute step counter `position`, honouring track length. */
export function trackStepAtGlobalPosition(track: Track, position: number): Step | undefined {
  if (track.length <= 0) return undefined;
  const index = ((position % track.length) + track.length) % track.length;
  return track.steps[index];
}

/**
 * A recorded parameter movement. Points are stored in beats from pattern start, so tempo
 * changes stretch automation correctly.
 *
 * The target mirrors Swift's synthesised `Codable` for an enum with associated values: a
 * single-key object whose key is the case name.
 */
export type AutomationTarget =
  | { padVolume: { padID: UUID } }
  | { padPan: { padID: UUID } }
  | { masterVolume: Record<string, never> }
  | { effectParameter: { nodeID: UUID; key: string } };

export type AutomationTargetKind = 'padVolume' | 'padPan' | 'masterVolume' | 'effectParameter';

export function automationTargetKind(target: AutomationTarget): AutomationTargetKind {
  if ('padVolume' in target) return 'padVolume';
  if ('padPan' in target) return 'padPan';
  if ('effectParameter' in target) return 'effectParameter';
  return 'masterVolume';
}

export function padVolumeTarget(padID: UUID): AutomationTarget {
  return { padVolume: { padID } };
}

export function padPanTarget(padID: UUID): AutomationTarget {
  return { padPan: { padID } };
}

export function masterVolumeTarget(): AutomationTarget {
  return { masterVolume: {} };
}

export function effectParameterTarget(nodeID: UUID, key: string): AutomationTarget {
  return { effectParameter: { nodeID, key } };
}

export interface AutomationPoint {
  beat: number;
  value: number;
}

export interface AutomationLane {
  id: UUID;
  target: AutomationTarget;
  points: AutomationPoint[];
  isEnabled: boolean;
}

export function makeAutomationLane(
  target: AutomationTarget,
  options: { id?: UUID; points?: AutomationPoint[]; isEnabled?: boolean } = {},
): AutomationLane {
  return {
    id: options.id ?? newUUID(),
    target,
    points: [...(options.points ?? [])].sort((a, b) => a.beat - b.beat),
    isEnabled: options.isEnabled ?? true,
  };
}

/** Linear interpolation between recorded points; holds the end values outside the range. */
export function automationValueAtBeat(lane: AutomationLane, beat: number): number | undefined {
  const points = lane.points;
  const first = points[0];
  if (first === undefined) return undefined;
  if (beat <= first.beat) return first.value;
  const last = points[points.length - 1]!;
  if (beat >= last.beat) return last.value;
  for (let index = 1; index < points.length; index++) {
    const previous = points[index - 1]!;
    const next = points[index]!;
    if (beat <= next.beat) {
      const span = next.beat - previous.beat;
      if (span <= 0) return next.value;
      const t = (beat - previous.beat) / span;
      return previous.value + (next.value - previous.value) * t;
    }
  }
  return last.value;
}

/** Thins a freshly recorded gesture so a 30-second sweep does not store 1800 points. */
export function simplifyAutomation(lane: AutomationLane, tolerance = 0.004): void {
  const points = lane.points;
  if (points.length <= 2) return;
  const kept: AutomationPoint[] = [points[0]!];
  for (let index = 1; index < points.length - 1; index++) {
    const previous = kept[kept.length - 1]!;
    const current = points[index]!;
    const next = points[index + 1]!;
    const span = next.beat - previous.beat;
    const expected =
      span > 0
        ? previous.value +
          ((next.value - previous.value) * (current.beat - previous.beat)) / span
        : current.value;
    if (Math.abs(current.value - expected) > tolerance) kept.push(current);
  }
  kept.push(points[points.length - 1]!);
  lane.points = kept;
}

export interface Pattern {
  id: UUID;
  name: string;
  tracks: Track[];
  /** Steps in the pattern, one of `PATTERN_LENGTH_OPTIONS`. */
  length: number;
  /** Steps per beat. 4 = sixteenth notes at 4/4. */
  stepsPerBeat: number;
  /** Overrides the project swing when defined. */
  swing?: number;
  automation: AutomationLane[];
}

export function makePattern(name: string, options: Partial<Omit<Pattern, 'name'>> = {}): Pattern {
  const pattern: Pattern = {
    id: options.id ?? newUUID(),
    name,
    tracks: options.tracks ?? [],
    length: options.length ?? PATTERN_LENGTH_DEFAULT,
    stepsPerBeat: options.stepsPerBeat ?? 4,
    automation: options.automation ?? [],
  };
  if (options.swing !== undefined) pattern.swing = options.swing;
  return pattern;
}

export function patternIsEmpty(pattern: Pattern): boolean {
  return pattern.tracks.every((track) => trackActiveStepCount(track) === 0);
}

/** Duration of one pass in beats. */
export function patternLengthInBeats(pattern: Pattern): number {
  return pattern.length / Math.max(1, pattern.stepsPerBeat);
}

export function sanitisePattern(pattern: Pattern): void {
  pattern.length = Math.round(
    clampFinite(pattern.length, 1, PATTERN_LENGTH_MAXIMUM, PATTERN_LENGTH_DEFAULT),
  );
  pattern.stepsPerBeat = Math.round(clampFinite(pattern.stepsPerBeat, 1, 8, 4));
  if (pattern.swing !== undefined) pattern.swing = clampFinite(pattern.swing, 0, 0.75, 0);
  if (!Array.isArray(pattern.tracks)) pattern.tracks = [];
  for (const track of pattern.tracks) sanitiseTrack(track);
}

export function patternAddTrack(
  pattern: Pattern,
  padID: UUID | undefined,
  name: string,
  color: PadColor,
): Track {
  const track = makeTrack({ padID, name, length: pattern.length, color });
  trackSetLength(track, pattern.length);
  pattern.tracks.push(track);
  return track;
}

export function duplicatePattern(pattern: Pattern, name: string): Pattern {
  return {
    ...pattern,
    id: newUUID(),
    name,
    tracks: pattern.tracks.map((track) => ({
      ...track,
      id: newUUID(),
      steps: track.steps.map((step) => ({ ...step })),
    })),
    automation: pattern.automation.map((lane) => ({
      ...lane,
      id: newUUID(),
      points: lane.points.map((point) => ({ ...point })),
    })),
  };
}

// MARK: - Scenes and song

/** A scene launches a specific pattern per bank, which is how a live set is performed. */
export interface Scene {
  id: UUID;
  name: string;
  patternID?: UUID;
  color: PadColor;
  /** Pads held on for the duration of the scene (a looping bed, for instance). */
  heldPadIDs: UUID[];
}

export function makeScene(name: string, options: Partial<Omit<Scene, 'name'>> = {}): Scene {
  const scene: Scene = {
    id: options.id ?? newUUID(),
    name,
    color: options.color ?? 'violet',
    heldPadIDs: options.heldPadIDs ?? [],
  };
  if (options.patternID !== undefined) scene.patternID = options.patternID;
  return scene;
}

export interface SongSection {
  id: UUID;
  patternID: UUID;
  repeatCount: number;
  name: string;
}

export function makeSongSection(
  patternID: UUID,
  options: { id?: UUID; repeatCount?: number; name?: string } = {},
): SongSection {
  return {
    id: options.id ?? newUUID(),
    patternID,
    repeatCount: Math.max(1, options.repeatCount ?? 1),
    name: options.name ?? '',
  };
}

export interface SongArrangement {
  sections: SongSection[];
  isEnabled: boolean;
}

export function makeSongArrangement(
  sections: SongSection[] = [],
  isEnabled = false,
): SongArrangement {
  return { sections, isEnabled };
}

export function songTotalRepeats(song: SongArrangement): number {
  return song.sections.reduce((total, section) => total + section.repeatCount, 0);
}

// MARK: - Project

export interface Project {
  id: UUID;
  schemaVersion: number;
  name: string;
  createdAt: ISODate;
  modifiedAt: ISODate;

  banks: Bank[];
  assets: SampleAsset[];
  patterns: Pattern[];
  song: SongArrangement;
  scenes: Scene[];

  tempo: number;
  /** 0…0.75. 0 is straight. */
  swing: number;
  timeSignatureBeats: number;
  master: MasterSettings;
  metronome: MetronomeSettings;

  artworkFileName?: string;
  notes: string;
  isArchived: boolean;
  /**
   * Set when the user deletes the project; the file survives until the recovery period
   * expires, so an accidental delete is undoable.
   */
  deletedAt?: ISODate;
}

/** Kept in step with `ProjectSchema.current`; `schema.ts` re-exports it as the public name. */
const DEFAULT_SCHEMA_VERSION = 1;

export function makeProject(name: string, options: Partial<Omit<Project, 'name'>> = {}): Project {
  const timestamp = nowISODate();
  const project: Project = {
    id: options.id ?? newUUID(),
    schemaVersion: options.schemaVersion ?? DEFAULT_SCHEMA_VERSION,
    name,
    createdAt: options.createdAt ?? timestamp,
    modifiedAt: options.modifiedAt ?? timestamp,
    banks: options.banks ?? [makeBank('Bank A')],
    assets: options.assets ?? [],
    patterns: options.patterns ?? [makePattern('Pattern 1')],
    song: options.song ?? makeSongArrangement(),
    scenes: options.scenes ?? [],
    tempo: options.tempo ?? 96,
    swing: options.swing ?? 0,
    timeSignatureBeats: options.timeSignatureBeats ?? 4,
    master: options.master ?? makeMasterSettings(),
    metronome: options.metronome ?? makeMetronomeSettings(),
    notes: options.notes ?? '',
    isArchived: options.isArchived ?? false,
  };
  if (options.artworkFileName !== undefined) project.artworkFileName = options.artworkFileName;
  if (options.deletedAt !== undefined) project.deletedAt = options.deletedAt;
  return project;
}

export function projectAsset(project: Project, id: UUID | undefined): SampleAsset | undefined {
  if (id === undefined) return undefined;
  return project.assets.find((asset) => asset.id === id);
}

export function projectPad(project: Project, id: UUID): Pad | undefined {
  for (const bank of project.banks) {
    const pad = bank.pads.find((entry) => entry.id === id);
    if (pad !== undefined) return pad;
  }
  return undefined;
}

export function projectBankIndexContainingPad(project: Project, id: UUID): number {
  return project.banks.findIndex((bank) => bank.pads.some((pad) => pad.id === id));
}

export function projectAllPads(project: Project): Pad[] {
  return project.banks.flatMap((bank) => bank.pads);
}

/**
 * Assets no pad references. Surfaced in storage management — never deleted automatically,
 * because an "orphan" is often a clip the user is about to re-assign.
 */
export function projectOrphanedAssetIds(project: Project): UUID[] {
  const referenced = new Set<UUID>();
  for (const pad of projectAllPads(project)) {
    if (pad.assetID !== undefined) referenced.add(pad.assetID);
  }
  return project.assets.map((asset) => asset.id).filter((id) => !referenced.has(id));
}

export function projectHasSoloedPads(project: Project): boolean {
  return projectAllPads(project).some((pad) => pad.isSoloed);
}

export function projectUpdatePad(
  project: Project,
  id: UUID,
  transform: (pad: Pad) => void,
): boolean {
  for (const bank of project.banks) {
    const pad = bank.pads.find((entry) => entry.id === id);
    if (pad !== undefined) {
      transform(pad);
      return true;
    }
  }
  return false;
}

export function projectUpdateAsset(
  project: Project,
  id: UUID,
  transform: (asset: SampleAsset) => void,
): boolean {
  const asset = project.assets.find((entry) => entry.id === id);
  if (asset === undefined) return false;
  transform(asset);
  return true;
}

export function touchProject(project: Project): void {
  project.modifiedAt = nowISODate();
}

/**
 * Repairs anything a corrupt file, a hand edit, or a migration could have broken. Called on
 * every load before the project reaches the engine. Mutates and returns the same object.
 */
export function sanitiseProject(project: Project): Project {
  project.tempo = clampFinite(project.tempo, 20, 300, 96);
  project.swing = clampFinite(project.swing, 0, 0.75, 0);
  project.timeSignatureBeats = Math.round(clampFinite(project.timeSignatureBeats, 1, 16, 4));
  if (typeof project.name !== 'string') project.name = 'Untitled';
  if (typeof project.notes !== 'string') project.notes = '';

  if (!project.master) project.master = makeMasterSettings();
  project.master.volume = clampFinite(project.master.volume, 0, 1, 1.0);
  project.master.headroomDB = clampFinite(project.master.headroomDB, -24, 0, 0);
  project.master.limiterCeilingDB = clampFinite(project.master.limiterCeilingDB, -12, 0, -0.3);
  // Every board made before the levels were fixed, brought up to the same loudness.
  liftLegacyMasterLevels(project.master);
  if (!project.master.chain) project.master.chain = emptyEffectChain();

  if (!project.metronome) project.metronome = makeMetronomeSettings();
  project.metronome.volume = clampFinite(project.metronome.volume, 0, 1, 0.6);
  project.metronome.countInBars = Math.round(clampFinite(project.metronome.countInBars, 0, 8, 1));

  if (!Array.isArray(project.banks) || project.banks.length === 0) {
    project.banks = [makeBank('Bank A')];
  }
  if (!Array.isArray(project.patterns) || project.patterns.length === 0) {
    project.patterns = [makePattern('Pattern 1')];
  }
  if (!Array.isArray(project.assets)) project.assets = [];
  if (!Array.isArray(project.scenes)) project.scenes = [];
  if (!project.song) project.song = makeSongArrangement();

  const assetsByID = new Map<UUID, SampleAsset>();
  for (const asset of project.assets) if (!assetsByID.has(asset.id)) assetsByID.set(asset.id, asset);

  for (const bank of project.banks) {
    if (!Array.isArray(bank.pads)) bank.pads = [];
    const capacity = gridCapacity(bank.grid);
    for (let slot = bank.pads.length; slot < capacity; slot++) bank.pads.push(makePad(slot));
    for (const pad of bank.pads) {
      pad.volume = clampFinite(pad.volume, 0, 1, 0.8);
      pad.pan = clampFinite(pad.pan, -1, 1, 0);
      pad.polyphony = Math.round(clampFinite(pad.polyphony, 1, 16, 4));
      pad.releaseFade = clampFinite(pad.releaseFade, 0, 5, 0.08);
      if (!pad.edit) pad.edit = sampleEditIdentity();
      if (!Array.isArray(pad.tags)) pad.tags = [];
      if (pad.assetID !== undefined) {
        const asset = assetsByID.get(pad.assetID);
        if (asset !== undefined) {
          clampSampleEdit(pad.edit, asset.duration);
        } else {
          // The asset vanished. Keep the pad's name and styling so the user can relink it,
          // but drop the dangling reference so nothing downstream chases a missing file.
          delete pad.assetID;
        }
      }
    }
  }

  for (const asset of project.assets) {
    asset.duration = clampFinite(asset.duration, 0, Number.MAX_SAFE_INTEGER, 0);
    if (!asset.defaultEdit) asset.defaultEdit = sampleEditIdentity();
    if (!Array.isArray(asset.tags)) asset.tags = [];
    if (!Array.isArray(asset.sliceMarkers)) asset.sliceMarkers = [];
    clampSampleEdit(asset.defaultEdit, asset.duration);
  }

  for (const pattern of project.patterns) sanitisePattern(pattern);

  normaliseEffectChain(project.master.chain);
  return project;
}

// MARK: - Project summary (list screens never decode a whole project)

export interface ProjectSummary {
  id: UUID;
  name: string;
  modifiedAt: ISODate;
  createdAt: ISODate;
  padCount: number;
  assetCount: number;
  tempo: number;
  artworkFileName?: string;
  byteSize: number;
  isArchived: boolean;
  deletedAt?: ISODate;
}

export function projectSummary(project: Project, byteSize: number): ProjectSummary {
  const summary: ProjectSummary = {
    id: project.id,
    name: project.name,
    modifiedAt: project.modifiedAt,
    createdAt: project.createdAt,
    padCount: projectAllPads(project).filter((pad) => !padIsEmpty(pad)).length,
    assetCount: project.assets.length,
    tempo: project.tempo,
    byteSize,
    isArchived: project.isArchived,
  };
  if (project.artworkFileName !== undefined) summary.artworkFileName = project.artworkFileName;
  if (project.deletedAt !== undefined) summary.deletedAt = project.deletedAt;
  return summary;
}
